import $axios from "../../api";

/**
 * Hak Akses Stock Opname (T2610-0044).
 *
 * Modul Vuex terpisah dari `StockOpname`: tidak berbagi satu pun state dengannya
 * (yang satu mengelola kunjungan, yang satu mengelola siapa boleh apa), dan
 * menumpangkannya berarti halaman Daftar Kunjungan ikut memuat ulang state panel
 * setiap kali filternya berubah.
 */
const state = {
  isLoading: false,
  saveLoading: false,
  deleteLoading: false,
  optionsLoading: false,
  historyLoading: false,
  errors: {},
  // Pesan penolakan BUKAN-validasi dari server (aturan bisnis 422: stocker tidak
  // bisa diatur, cakupan kosong). Disimpan terpisah dari `errors` karena
  // bentuknya berbeda — `errors` itu peta field→pesan milik Laravel, sementara
  // ini satu kalimat yang tidak menempel ke isian mana pun.
  errorMessage: "",

  list: {
    data: [],
    total: 0,
    per_page: 20,
    from: 0,
    to: 0,
  },
  page: 1,

  // Form Atur Akses untuk satu orang.
  detail: null,

  // Opsi modal "Tambah Area dan Gudang" + picker Karyawan + katalog aksi.
  // Dimuat SEKALI per kunjungan halaman: daftarnya ribuan baris gudang dan
  // penyaringannya dikerjakan di klien, mengikuti keputusan yang sama pada
  // picker Lokasi di Atur Kunjungan (lihat index.vue:164-166).
  options: {
    areas: [],
    warehouses: [],
    actions: [],
    people: [],
    viewAction: { label: "Lihat Kunjungan", hint: "" },
  },

  history: [],
};

const getters = {
  /** Aksi yang tersedia, dalam urutan kanonik dari server (bukan alfabetis). */
  actionCatalog: (state) => state.options.actions,
};

const mutations = {
  SET_LOADING(state, value) {
    state.isLoading = value;
  },
  SET_SAVE_LOADING(state, value) {
    state.saveLoading = value;
  },
  SET_DELETE_LOADING(state, value) {
    state.deleteLoading = value;
  },
  SET_OPTIONS_LOADING(state, value) {
    state.optionsLoading = value;
  },
  SET_HISTORY_LOADING(state, value) {
    state.historyLoading = value;
  },
  SET_ERRORS(state, value) {
    state.errors = value || {};
  },
  SET_ERROR_MESSAGE(state, value) {
    state.errorMessage = value || "";
  },
  SET_LIST(state, payload) {
    state.list = payload;
  },
  SET_PAGE(state, value) {
    state.page = value;
  },
  SET_DETAIL(state, payload) {
    state.detail = payload;
  },
  RESET_DETAIL(state) {
    state.detail = null;
  },
  SET_OPTIONS(state, payload) {
    state.options = {
      areas: payload?.areas || [],
      warehouses: payload?.warehouses || [],
      actions: payload?.actions || [],
      people: payload?.people || [],
      // Baris "Lihat Kunjungan" yang tercentang + disabled. Bawaannya objek
      // kosong, bukan null — templat membacanya langsung tanpa penjaga.
      viewAction: payload?.view_action || { label: "Lihat Kunjungan", hint: "" },
    };
  },
  SET_HISTORY(state, payload) {
    state.history = payload || [];
  },
};

const actions = {
  getList({ commit, state, dispatch }, payload = "") {
    commit("SET_LOADING", true);

    return $axios
      .get(`inv/stock-opname/access/list?page=${state.page}&view=${state.list.per_page}${payload}`)
      .then((res) => {
        commit("SET_LIST", res.data.data);
        return res;
      })
      .catch((error) => {
        dispatch("Main/globalSweetAlert", error.response, { root: true });
        throw error;
      })
      .finally(() => {
        commit("SET_LOADING", false);
      });
  },

  getDetail({ commit, dispatch }, userId) {
    commit("SET_LOADING", true);
    commit("RESET_DETAIL");

    return $axios
      .get(`inv/stock-opname/access/view/${userId}`)
      .then((res) => {
        commit("SET_DETAIL", res.data.data);
        return res;
      })
      .catch((error) => {
        dispatch("Main/globalSweetAlert", error.response, { root: true });
        throw error;
      })
      .finally(() => {
        commit("SET_LOADING", false);
      });
  },

  getOptions({ commit, dispatch }) {
    commit("SET_OPTIONS_LOADING", true);

    return $axios
      .get("inv/stock-opname/access/scope-options")
      .then((res) => {
        commit("SET_OPTIONS", res.data.data);
        return res;
      })
      .catch((error) => {
        dispatch("Main/globalSweetAlert", error.response, { root: true });
        throw error;
      })
      .finally(() => {
        commit("SET_OPTIONS_LOADING", false);
      });
  },

  save({ commit, dispatch }, payload) {
    commit("SET_SAVE_LOADING", true);
    commit("SET_ERRORS", null);
    commit("SET_ERROR_MESSAGE", "");

    return $axios
      .post("inv/stock-opname/access/save", payload)
      .then((res) => {
        return res;
      })
      .catch((error) => {
        // 422 dipegang halaman supaya pesannya tampil inline, bukan hilang
        // bersama popup. Dua bentuk yang mungkin, dan keduanya harus tertangkap:
        //   - `errors`  → validasi Laravel, peta field→pesan
        //   - `message` → aturan bisnis controller (stocker, cakupan kosong)
        if (error.response?.status === 422) {
          commit("SET_ERRORS", error.response.data?.errors || {});
          commit("SET_ERROR_MESSAGE", error.response.data?.message || "");
        } else {
          dispatch("Main/globalSweetAlert", error.response, { root: true });
        }
        throw error;
      })
      .finally(() => {
        commit("SET_SAVE_LOADING", false);
      });
  },

  remove({ commit, dispatch }, userId) {
    commit("SET_DELETE_LOADING", true);

    return $axios
      .post("inv/stock-opname/access/delete", { user_id: userId })
      .then((res) => {
        dispatch("Main/globalSuccessSweetAlert", res.data?.message, { root: true });
        return res;
      })
      .catch((error) => {
        dispatch("Main/globalSweetAlert", error.response, { root: true });
        throw error;
      })
      .finally(() => {
        commit("SET_DELETE_LOADING", false);
      });
  },

  getHistory({ commit, dispatch }, userId) {
    commit("SET_HISTORY_LOADING", true);
    commit("SET_HISTORY", []);

    return $axios
      .get(`inv/stock-opname/access/history/${userId}`)
      .then((res) => {
        commit("SET_HISTORY", res.data.data);
        return res;
      })
      .catch((error) => {
        dispatch("Main/globalSweetAlert", error.response, { root: true });
        throw error;
      })
      .finally(() => {
        commit("SET_HISTORY_LOADING", false);
      });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
